import { APP_INITIALIZER, ApplicationConfig, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { accessTokenInterceptor } from '@core/interceptors/access-token/access-token.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { configFactory } from '@shared/services/configsService/configs.factory';
import { ConfigRepo } from '@shared/services/configsService/config-repo.service';
import { languageFactory } from './components/languages/language.factory';
import { LanguageRepo } from './components/languages/language.repo';
import { ThemeService } from '@shared/services/themeService/theme.service';
import { BaseApiService } from '@shared/services/apiService/base-api.service';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { PrimeNGConfig } from 'primeng/api';
import { primengLocalizationFactory } from '@shared/services/primengLocalizationFactory';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe } from '@angular/common';
import { AutoResizeTextAreaDirective } from './directives/autoResizeTextArea';
import { FullCalendarModule } from '@fullcalendar/angular';
import { LanguagesApiDTO } from './components/languages/languages.dto';
import { ConfigApiDTO } from '@shared/services/configsService/config.dto';
import { loggingInterceptor } from '@shared/interceptors/loggingInterceptor';
import { catchInterceptor } from '@shared/interceptors/catchInterceptor';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptors([loggingInterceptor, accessTokenInterceptor, catchInterceptor])),
    AutoResizeTextAreaDirective,
    FullCalendarModule,
    ConfigRepo,
    LanguageRepo,
    BaseApiService,
    ThemeService,
    DatePipe,
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    provideTransloco({
      config: {
        availableLangs: ['en', 'es', 'ca', 'eu'],
        defaultLang: 'en',
        fallbackLang: ['en', 'es'],
        missingHandler: {
          useFallbackTranslation: true,
        },
        flatten: {
          // aot: !isDevMode(), // DONT ACTIVATE THIS, transloco-optimize lib probably needed
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    { // This is only used to not explicitly set format for input datetime-local
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'yyyy-MM-ddTHH:mm' },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: primengLocalizationFactory,
      deps: [TranslocoService, PrimeNGConfig],
      multi: true,
    },
    // On launch routine
    { provide: 'LanguagesApiService', useFactory: () => new BaseApiService<LanguagesApiDTO>('languages') },
    { provide: 'configApiService', useFactory: () => new BaseApiService<ConfigApiDTO>('configs') },
    {
      provide: APP_INITIALIZER,
      useFactory: configFactory,
      deps: ['configApiService', ConfigRepo],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: languageFactory,
      deps: ['LanguagesApiService', LanguageRepo],
      multi: true,
    },
  ],
};

